// The attention feed board: tabs, the round header, then one list: "Needs you" (a few rows),
// "Waiting" (the rest of the round, each bundle of alike cards as one "Together" entry) and one
// combined routine cleanup. Other tabs list their routine groups and cards as before. Wide screens
// show the selected entry beside the list (nothing moves while you read it; a bundle member
// pressed there opens in its place); narrow screens open a card on its own page. Adapted in
// spirit from EveryInc/tend src/App.tsx (MIT, Copyright (c) 2026 Every, Inc.), decomposed into
// routes and hooks.
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { getRouteApi, Link } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type { CardSummary, CardView } from "@shared/api";
import { useAccountLookup } from "@web/lib/accounts";
import { isApiError } from "@web/lib/api";
import { feedApi } from "@web/lib/feedApi";
import { feedKeys, prefetchCard } from "@web/lib/feedQueries";
import { markCardSwitch, useCardPainted } from "@web/measure/useMeasure";
import { useVerdictCommand } from "@web/measure/verdictStore";
import { cardTarget } from "@web/measure/verdicts";
import { useMediaQuery, WIDE_QUERY } from "@web/lib/useMediaQuery";
import { useNow } from "@web/lib/useNow";
import { useCommand } from "@web/shell/commands";
import { dock } from "@web/shell/dockStore";
import { Button } from "@web/ui/Button";
import { SwipeableCardRow } from "@web/notify/SwipeableCardRow";
import { FeedPickUp } from "@web/focus/PickUp";
import { FreshStartNudge } from "./FreshStartNudge";
import { ActiveCard } from "./ActiveCard";
import { Calm } from "./Calm";
import { BoardTabs, LearningOffer, PassBar, RowSection, rowId, useNextPass } from "./BoardParts";
import { BundleBlock, BundleRow } from "./BundleBlock";
import { CLEANUP_GROUP_ID, CLEANUP_NAV_ID, CleanupBlock, CleanupRow } from "./CleanupBlock";
import { RoutineGroupBlock, RoutineGroupRow } from "./RoutineGroupBlock";
import { NextRoundOffer } from "./RoundStart";
import { SnoozedTray } from "./SnoozedTray";
import {
  bundleIdOfNavId,
  bundleNavId,
  entryNavId,
  groupIdOfNavId,
  passCount,
  roundRows,
  routineNavId,
  shouldVirtualize,
  TAB_LABEL,
  type BoardTab,
  type BoardBundle,
  type RoundEntry,
  type RoundRows,
} from "./selectors";
import { ThreadDrawer } from "./ThreadDrawer";
import { useBoardModel, useSelectedCard } from "./useBoardModel";
import { needsYouWords, waitingWords } from "./wording";
import { VirtualCardList } from "./VirtualCardList";

const route = getRouteApi("/_app/feed");

const EMPTY: Record<BoardTab, { title: string; body: string }> = {
  review: {
    title: "End of this round",
    body: "Nothing is waiting for you in this round. New mail waits for the next one.",
  },
  progress: {
    title: "Nothing in progress",
    body: "What you approve or hand to Claude waits here until it is done.",
  },
  done: { title: "Nothing done yet", body: "Decided cards stay here for a while." },
};

function SkeletonRows() {
  return (
    <div aria-busy className="flex flex-col">
      {Array.from({ length: 5 }, (_, i) => (
        <div
          key={i}
          aria-hidden
          className="flex flex-col gap-2 border-b border-line bg-paper px-5 py-4"
        >
          <div className="h-2.5 w-24 rounded bg-sunk" />
          <div className="h-4 w-3/4 rounded bg-sunk" />
          <div className="h-3 w-5/6 rounded bg-sunk" />
        </div>
      ))}
    </div>
  );
}

export function FeedPage() {
  const search = route.useSearch();
  const navigate = route.useNavigate();
  const queryClient = useQueryClient();
  const tab: BoardTab = search.tab ?? "review";
  const model = useBoardModel();
  const { board, sections, feedId } = model;
  const wide = useMediaQuery(WIDE_QUERY);
  const now = useNow(60_000);
  const { list: accounts, colorIndex, label } = useAccountLookup();
  const [drawer, setDrawer] = useState(false);
  const next = useNextPass(feedId);
  const listRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const review = tab === "review";
  const cards = useMemo(() => (sections ? sections[tab] : []), [sections, tab]);
  // To review: "Needs you" (a few rows) leads, then the rest of the round ("Waiting").
  // A bundle of alike cards is one entry in the place of its first member.
  const rows = useMemo(
    () =>
      sections && review
        ? roundRows(sections.review, sections.breakthrough, sections.bundles)
        : null,
    [sections, review],
  );
  // Every card row in list (and j/k) order; bundle members are inside their bundle's entry.
  const listCards = useMemo(
    () => (rows ? [...rows.needsYou, ...rows.waiting] : cards),
    [rows, cards],
  );
  // To review shows every proposed routine group as one cleanup entry, after the cards; the
  // other tabs list their groups one by one, above the cards.
  const cleanupGroups = useMemo(
    () => (sections && review ? sections.routine.review : []),
    [sections, review],
  );
  const hasCleanup = cleanupGroups.length > 0;
  const routine = useMemo(
    () => (sections && !review ? sections.routine[tab] : []),
    [sections, review, tab],
  );
  const navIds = useMemo(
    () =>
      review
        ? [
            ...(rows?.needsYou.map((c) => c.id) ?? []),
            ...(rows?.entries.map(entryNavId) ?? []),
            ...(hasCleanup ? [CLEANUP_NAV_ID] : []),
          ]
        : [...routine.map((g) => routineNavId(g.id)), ...listCards.map((c) => c.id)],
    [review, rows, listCards, hasCleanup, routine],
  );
  // Selection starts at the first card (a cleanup above the cards is one k away).
  const sel = useSelectedCard(feedId, tab, navIds, !review && cards.length ? routine.length : 0);
  const selectedGroupId = groupIdOfNavId(sel.selectedId);
  const cleanupSelected = review && hasCleanup && selectedGroupId === CLEANUP_GROUP_ID;
  const selectedGroup =
    !review && selectedGroupId ? (routine.find((g) => g.id === selectedGroupId) ?? null) : null;
  const selectedBundleId = bundleIdOfNavId(sel.selectedId);
  const selectedBundle =
    review && selectedBundleId
      ? (sections?.bundles.find((b) => b.id === selectedBundleId) ?? null)
      : null;
  // A bundle member pressed in the pane is handled there on its own, until j/k move on.
  const [solo, setSolo] = useState<{ bundleId: string; cardId: string } | null>(null);
  const soloCard =
    selectedBundle && solo?.bundleId === selectedBundle.id
      ? (selectedBundle.cards.find((c) => c.id === solo.cardId) ?? null)
      : null;
  const selected =
    soloCard ??
    (selectedGroupId || selectedBundleId
      ? null
      : (listCards.find((c) => c.id === sel.selectedId) ?? null));
  const cardIndex = selected ? listCards.indexOf(selected) : -1;
  const ready = board?.pass.readyNext.count ?? 0;
  const virtual = shouldVirtualize(tab, cards.length);

  useEffect(() => dock.prefer("card"), []);

  // Warm the next few cards so j feels instant.
  useEffect(() => {
    for (const card of listCards.slice(cardIndex + 1, cardIndex + 4))
      prefetchCard(queryClient, card.id);
  }, [listCards, cardIndex, queryClient]);

  // Keep the selected row in view when j/k move it (not on arrival: the page starts at the top).
  const lastSelected = useRef(sel.selectedId);
  useEffect(() => {
    if (!sel.selectedId || lastSelected.current === sel.selectedId) return;
    const first = lastSelected.current === null;
    lastSelected.current = sel.selectedId;
    if (first) return;
    document.getElementById(rowId(sel.selectedId))?.scrollIntoView({ block: "nearest" });
  }, [sel.selectedId]);

  useEffect(() => {
    if (document.activeElement === document.body) listRef.current?.focus({ preventScroll: true });
  }, []);

  const openCard = (card: CardSummary) => {
    // A bundle member has no row of its own: the selection stays on its bundle.
    if (navIds.includes(card.id)) sel.select(card.id);
    void navigate({ to: "/feed/cards/$cardId", params: { cardId: card.id } });
  };
  const onRow = (card: CardSummary) => (wide ? sel.select(card.id) : openCard(card));
  const onBundleMember = (bundle: BoardBundle, cardId: string) => {
    if (wide) setSolo({ bundleId: bundle.id, cardId });
    else void navigate({ to: "/feed/cards/$cardId", params: { cardId } });
  };
  const setTab = (t: BoardTab) =>
    void navigate({ search: { tab: t === "review" ? undefined : t }, replace: true });

  const recollect = async () => {
    if (!feedId) return;
    try {
      await feedApi.recollect(feedId);
      toast("Claude is looking again. New cards wait for the next round.");
    } catch (error) {
      toast(
        isApiError(error, "not_available")
          ? "Claude isn't wired up yet."
          : "Couldn't start that. Try again.",
      );
    }
  };

  // j/k time the switch until the next card paints (local measure, U0).
  const move = (delta: 1 | -1) => {
    setSolo(null);
    markCardSwitch(sel.selectedId);
    void sel.move(delta);
  };
  useCardPainted(sel.selectedId);
  useCommand("card.next", () => move(1), navIds.length > 0);
  useCommand("card.prev", () => move(-1), navIds.length > 0);
  // Narrow screens list cards without opening them: `!` judges the selected row.
  useVerdictCommand(() => {
    if (!selected) return null;
    const full = queryClient.getQueryData<CardView>(feedKeys.card(selected.id));
    return cardTarget(full ?? selected, "card");
  }, !wide && !!selected);
  useCommand("card.open", () => (selected ? openCard(selected) : undefined), !!selected);
  useCommand("card.thread", () => setDrawer((d) => !d), !!selected);
  useCommand("card.cancel", () => setDrawer(false), drawer);
  useCommand("feed.focus", () => void navigate({ to: "/focus" }));
  useCommand("feed.nextPass", () => {
    if (ready > 0) void next.start();
    else toast("Nothing new is waiting for the next round yet.");
  });
  useCommand("feed.recollect", () => void recollect(), !!feedId);

  const cardRow = (card: CardSummary, needsYou = false) => (
    <SwipeableCardRow
      key={card.id}
      card={card}
      selected={card.id === sel.selectedId}
      showAccount={accounts.length > 1}
      colorIndex={card.accountId ? colorIndex(card.accountId) : 0}
      accountLabel={card.accountEmail ? label(card.accountEmail) : undefined}
      now={now}
      onSelect={onRow}
      needsYou={needsYou}
    />
  );

  let list: ReactNode;
  if (model.noFeed) {
    list = (
      <Calm
        title="Your feed is getting ready"
        body="Once an account is connected and Claude has read your mail, cards appear here."
      />
    );
  } else if (model.isPending) {
    list = <SkeletonRows />;
  } else if (model.isError && !board) {
    list = (
      <Calm title="Couldn’t load the feed" body="The server didn’t answer. Nothing is lost.">
        <Button onClick={model.refetch}>Try again</Button>
      </Calm>
    );
  } else if (!navIds.length) {
    list = (
      <Calm {...EMPTY[tab]}>
        {tab === "review" && ready > 0 && board ? (
          <NextRoundOffer
            estimate={board.pass.nextEstimate}
            busy={next.busy}
            onStart={(order) => void next.start(order)}
            size="md"
          />
        ) : tab === "review" ? (
          <Link to="/fresh-start" className="text-sm text-muted underline underline-offset-2">
            Older mail in the way? Fresh start archives it.
          </Link>
        ) : null}
      </Calm>
    );
  } else {
    list = (
      <>
        {routine.length && !wide ? (
          <div className="flex flex-col gap-3 px-4 py-3 md:px-5">
            {routine.map((g) => {
              const navId = routineNavId(g.id);
              const on = navId === sel.selectedId;
              return (
                <RoutineGroupBlock
                  key={g.id}
                  id={rowId(navId)}
                  group={g}
                  active={on}
                  selected={on}
                  onSelect={() => sel.select(navId)}
                />
              );
            })}
          </div>
        ) : null}
        <div
          ref={listRef}
          role="listbox"
          aria-label={`Cards: ${TAB_LABEL[tab]}`}
          tabIndex={0}
          aria-activedescendant={sel.selectedId ? rowId(sel.selectedId) : undefined}
          className="border-t border-line outline-none"
        >
          {wide
            ? routine.map((g) => {
                const navId = routineNavId(g.id);
                return (
                  <RoutineGroupRow
                    key={g.id}
                    id={rowId(navId)}
                    group={g}
                    selected={navId === sel.selectedId}
                    onSelect={() => sel.select(navId)}
                  />
                );
              })
            : null}
          {rows ? (
            <ReviewRows
              rows={rows}
              cardRow={cardRow}
              bundleEntry={(bundle) => {
                const navId = bundleNavId(bundle.id);
                const on = navId === sel.selectedId;
                return wide ? (
                  <BundleRow
                    key={navId}
                    bundle={bundle}
                    id={rowId(navId)}
                    selected={on}
                    onSelect={() => {
                      setSolo(null);
                      sel.select(navId);
                    }}
                  />
                ) : (
                  // Narrow screens show the whole bundle in its place in the list.
                  <div key={navId} className="border-b border-line px-4 py-3 md:px-5">
                    <BundleBlock
                      bundle={bundle}
                      id={rowId(navId)}
                      selected={on}
                      onSelect={() => sel.select(navId)}
                      onSelectCard={(cardId) => onBundleMember(bundle, cardId)}
                    />
                  </div>
                );
              }}
              cleanup={
                wide && hasCleanup ? (
                  <CleanupRow
                    groups={cleanupGroups}
                    id={rowId(CLEANUP_NAV_ID)}
                    selected={CLEANUP_NAV_ID === sel.selectedId}
                    onSelect={() => sel.select(CLEANUP_NAV_ID)}
                  />
                ) : null
              }
            />
          ) : virtual ? (
            <VirtualCardList
              cards={cards}
              scrollRef={scrollRef}
              selectedId={sel.selectedId}
              showAccount={accounts.length > 1}
              colorIndex={colorIndex}
              accountLabel={label}
              now={now}
              onSelect={onRow}
            />
          ) : (
            cards.map((card) => cardRow(card))
          )}
        </div>
        {!wide && hasCleanup ? (
          <div className="px-4 py-3 md:px-5">
            <CleanupBlock
              groups={cleanupGroups}
              id={rowId(CLEANUP_NAV_ID)}
              active={cleanupSelected}
              selected={cleanupSelected}
              onSelect={() => sel.select(CLEANUP_NAV_ID)}
            />
          </div>
        ) : null}
        {tab === "review" ? (
          <div className="flex flex-col items-center gap-2 px-4 py-6 text-center">
            <p className="mm-kicker">End of this round</p>
            {ready > 0 && board ? (
              <NextRoundOffer
                estimate={board.pass.nextEstimate}
                busy={next.busy}
                onStart={(order) => void next.start(order)}
                emphasis="secondary"
              />
            ) : (
              <p className="text-sm text-muted">New mail waits for the next round.</p>
            )}
          </div>
        ) : null}
      </>
    );
  }

  // After the end of the round: the learning offer, then what is snoozed and when it comes back
  // (folded, no count).
  const tray =
    board && feedId && review ? (
      <>
        {board.learningOffer ? <LearningOffer feedId={feedId} /> : null}
        <SnoozedTray feedId={feedId} />
      </>
    ) : null;

  const passBar =
    board && sections && review ? (
      <PassBar
        board={board}
        count={passCount(sections)}
        roundCards={sections.review}
        progress={model.progress}
      />
    ) : null;

  const notices =
    board && sections ? (
      <>
        {review ? <FreshStartNudge feedId={feedId} /> : null}
        {review ? (
          <FeedPickUp
            feedId={feedId}
            currentPass={board.pass.current}
            hasOpenCards={sections.review.length > 0}
          />
        ) : null}
        {board.sweep.recollectionOffered ? (
          <div className="mx-4 mt-3 flex flex-wrap items-center gap-2 text-sm text-muted md:mx-5">
            <span>Claude may have missed something.</span>
            <button
              type="button"
              className="text-accent-text underline"
              onClick={() => void recollect()}
            >
              Look again
            </button>
          </div>
        ) : null}
        <div className="h-3" />
      </>
    ) : null;

  return (
    <div className="flex h-full flex-col">
      <BoardTabs tab={tab} onTab={setTab} sections={sections} budget={board?.budget} />
      {wide ? (
        <>
          {passBar ? <div className="shrink-0 pb-3">{passBar}</div> : null}
          <div className="grid min-h-0 flex-1 grid-cols-[minmax(300px,380px)_minmax(0,1fr)] border-t border-line">
            <div
              ref={scrollRef}
              className="min-h-0 overflow-y-auto border-r border-line bg-paper/40"
            >
              {notices}
              {list}
              {tray}
            </div>
            <div className="min-h-0 overflow-y-auto">
              {selectedBundle && soloCard && board ? (
                <div className="mx-auto max-w-3xl px-6 pt-3 pb-16">
                  <button
                    type="button"
                    onClick={() => setSolo(null)}
                    className="mm-tap mb-2 inline-flex items-center gap-1.5 rounded-md py-1 pr-2 text-sm text-muted hover:text-ink"
                  >
                    <ArrowLeft aria-hidden className="size-4" />
                    {selectedBundle.label}
                  </button>
                  <ActiveCard
                    key={soloCard.id}
                    summary={soloCard}
                    feed={board.feed}
                    active
                    onToggleThread={() => setDrawer((d) => !d)}
                    onEscape={() => {
                      setSolo(null);
                      return true;
                    }}
                    canEscape
                  />
                </div>
              ) : selectedBundle ? (
                <div className="mx-auto max-w-3xl px-6 pt-5 pb-16">
                  <BundleBlock
                    key={selectedBundle.id}
                    bundle={selectedBundle}
                    selected
                    onSelectCard={(cardId) => onBundleMember(selectedBundle, cardId)}
                  />
                </div>
              ) : cleanupSelected ? (
                // Room below so a confirmation never sits under the Dock.
                <div className="mx-auto max-w-3xl px-6 pt-5 pb-16">
                  <CleanupBlock groups={cleanupGroups} active defaultOpen />
                </div>
              ) : selectedGroup ? (
                // Room below so a confirmation never sits under the Dock.
                <div className="mx-auto max-w-3xl px-6 pt-5 pb-16">
                  <RoutineGroupBlock
                    key={selectedGroup.id}
                    group={selectedGroup}
                    active
                    defaultOpen
                  />
                </div>
              ) : selected && board ? (
                <div className="mx-auto max-w-3xl px-6 pt-5 pb-16">
                  <ActiveCard
                    key={selected.id}
                    summary={selected}
                    feed={board.feed}
                    active
                    onToggleThread={() => setDrawer((d) => !d)}
                  />
                </div>
              ) : (
                <Calm
                  title={navIds.length ? "Pick a card" : "Nothing to show here"}
                  body={
                    navIds.length
                      ? "j and k move, Space does what Claude suggests, or click a card on the left."
                      : "Other tabs may have something."
                  }
                />
              )}
            </div>
          </div>
        </>
      ) : (
        <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto">
          {passBar}
          {notices}
          {list}
          {tray}
        </div>
      )}
      {drawer && selected ? (
        <ThreadDrawer
          key={selected.id}
          cardId={selected.id}
          threadKey={selected.threadKey}
          title={selected.title}
          onClose={() => setDrawer(false)}
        />
      ) : null}
    </div>
  );
}

/**
 * The To review list: "Needs you · N" with at most three rows (and "N more need you, below"
 * when there are more), then "Waiting · N" with the rest of the round (a bundle counts as one
 * entry), then the cleanup entry.
 * Without anything that needs you, the waiting rows stand alone under the round header.
 */
function ReviewRows({
  rows,
  cardRow,
  bundleEntry,
  cleanup,
}: {
  rows: RoundRows;
  cardRow: (card: CardSummary, needsYou?: boolean) => ReactNode;
  bundleEntry: (bundle: BoardBundle) => ReactNode;
  cleanup: ReactNode;
}) {
  const needs = needsYouWords(rows.needsYouTotal, rows.moreBelow);
  const entry = (e: RoundEntry) => (e.kind === "card" ? cardRow(e.card) : bundleEntry(e.bundle));
  return (
    <>
      {rows.needsYou.length ? (
        <RowSection id="feed-needs-you" label={needs.label} accent note={needs.note}>
          {rows.needsYou.map((card) => cardRow(card, true))}
        </RowSection>
      ) : null}
      {rows.entries.length && rows.needsYou.length ? (
        <RowSection id="feed-waiting" label={waitingWords(rows.entries.length)}>
          {rows.entries.map(entry)}
        </RowSection>
      ) : (
        rows.entries.map(entry)
      )}
      {cleanup}
    </>
  );
}
